import { capitalize } from "lodash";
import pluralize from "pluralize";
import type { AmpLibraryFileFragment } from "src/graphql";
import { runBatch } from "src/pages/projects/library/run-batch";

const CONCURRENCY = 5;

type Verb = { present: string; past: string };

export const MOVE: Verb = { present: "move", past: "moved" };
export const DELETE: Verb = { present: "delete", past: "deleted" };

/** Runs one action per file and reports which ones succeeded. */
export async function forEachFile(
  files: AmpLibraryFileFragment[],
  run: (file: AmpLibraryFileFragment) => Promise<unknown>,
) {
  const { succeeded, failed } = await runBatch(files, CONCURRENCY, run);
  return {
    done: succeeded.map(({ item }) => item),
    failed: failed.length,
    error: failed[0]?.error,
  };
}

/** The toast for a batch: what happened to all of it, some of it, or none of it. */
export function bulkResultToast({
  verb,
  total,
  done,
  error,
  where,
}: {
  verb: Verb;
  total: number;
  done: number;
  error: unknown;
  /** Where the files went, for the success message. */
  where?: string;
}) {
  const id = `library-${verb.present}`;
  if (done === total)
    return {
      id,
      variant: "success" as const,
      title: `${capitalize(verb.past)} ${pluralize("file", done, true)}${where ? ` to ${where}` : ""}`,
    };
  if (done === 0)
    return {
      id,
      variant: "error" as const,
      title: `Could not ${verb.present} ${pluralize("file", total)}`,
      message: error instanceof Error ? error.message : "Try again.",
    };
  const failed = total - done;
  return {
    id,
    variant: "warning" as const,
    title: `${capitalize(verb.past)} ${done} of ${pluralize("file", total, true)}`,
    message: `${pluralize("file", failed, true)} could not be ${verb.past}. Try again to ${verb.present} ${failed === 1 ? "it" : "them"}.`,
  };
}
